/** React */
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "moment/locale/es";

/** MUI icons */
import DeleteIcon from "@mui/icons-material/Delete";

/** MUI */
import {
    Checkbox,
    Grid,
    MenuItem,
    Typography,
} from "@mui/material";

/** Components */
import BaseSelect2 from "../ui/BaseSelect2";
import CustomDatePicker from "../ui/CustomDatePicker";
import CustTextField from "../ui/CustTextField";
import dashboardService from "../../services/dashboard";
import FiltersPopover from "../ui/FiltersPopover";

const defaultFilter = {
    filtersArray: [
        {
            type: "",
            value: "",
            since: "",
            until: "",
            multiple: [],
        },
    ],
};

const FiltersButton = (props) => {
    const { banks, setShowSkeleton, franchise, company, setDashboard, grupal, companiesFilters, showSkeleton } = props;
    const [t] = useTranslation("global");

    const [anchorEl, setAnchorEl] = useState(null);
    const [filters, setFilters] = useState(defaultFilter);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? "simple-popover" : undefined;

    const attributes =
        (franchise && !company) || grupal
            ? [
                  { id: 1, name: t("dashboard.company"), query: "company_id" },
                  { id: 2, name: t("dashboard.bank"), query: "bank_id" },
                  { id: 3, name: t("dashboard.lastTrans"), query: "transaction" },
                  { id: 4, name: t("dashboard.lastPeriod"), query: "period" },
                  { id: 5, name: t("dashboard.latestBalance"), query: "balance" },
              ]
            : [
                  { id: 2, name: t("dashboard.bank"), query: "bank_id" },
                  { id: 3, name: t("dashboard.lastTrans"), query: "transaction" },
                  { id: 4, name: t("dashboard.lastPeriod"), query: "period" },
                  { id: 5, name: t("dashboard.latestBalance"), query: "balance" },
              ];

    const limit = (franchise && !company) || grupal ? 5 : 4;

    const addFilter = () => {
        let array = [...filters.filtersArray];
        if (array.length < limit) {
            setFilters({
                ...filters,
                filtersArray: [
                    ...filters.filtersArray,
                    {
                        type: "",
                        value: "",
                        since: "",
                        until: "",
                        multiple: [],
                    },
                ],
            });
        }
    };

    const removeFilter = async (index) => {
        let array = [...filters.filtersArray];

        if (index !== -1) {
            array.splice(index, 1);
            setFilters({
                ...filters,
                filtersArray: array,
            });

            setShowSkeleton(true);
            let objectFinal = {};

            array.forEach((item) => {
                const query = attributes.find((item2) => item2.id === item.type).query;
                if (item.value && item.type) {
                    objectFinal[query] = item.value;
                }
                if (item.type && item.since) {
                    objectFinal[query + "_since"] = query !== "balance" ? item.since.format("YYYY-MM-DD") : item.since;
                }
                if (item.type && item.until) {
                    objectFinal[query + "_until"] = query !== "balance" ? item.until.format("YYYY-MM-DD") : item.until;
                }
                if (item.multiple && item.multiple.length > 0) {
                    objectFinal[query] = item.multiple;
                }
            });

            await dashboardService
                .getDashboard({
                    company_id: company ?? undefined,
                    franchise_id: franchise ?? undefined,
                    grouped: grupal ?? false,
                    ...objectFinal,
                })
                .then((response) => {
                    if (response.length > 0) {
                        setDashboard(response);
                    } else {
                        setDashboard([]);
                    }
                })
                .catch((err) => {
                    console.log(err);
                });

            setShowSkeleton(false);
        }
    };

    const removeFirstFilter = async () => {
        setShowSkeleton(true);
        setFilters({
            filtersArray: [
                {
                    type: "",
                    value: "",
                    since: "",
                    until: "",
                    multiple: [],
                },
            ],
        });

        await dashboardService
            .getDashboard({
                company_id: company ?? undefined,
                franchise_id: franchise ?? undefined,
                grouped: grupal ?? false,
            })
            .then((response) => {
                if (response.length > 0) {
                    setDashboard(response);
                } else {
                    setDashboard([]);
                }
            })
            .catch((err) => {
                console.log(err);
            });

        setShowSkeleton(false);
    };

    const handleChangeType = (event, index) => {
        let aux = [...filters.filtersArray];
        aux[index].type = event.target.value;
        aux[index].value = "";
        aux[index].since = "";
        aux[index].until = "";
        aux[index].multiple = [];

        setFilters({
            ...filters,
            filtersArray: aux,
        });
    };

    const handleChangeFilter = async (event, index, filterType) => {
        const aux = [...filters.filtersArray];

        if (filterType === 1 || filterType === 2) {
            aux[index].value = "";
            aux[index].since = "";
            aux[index].until = "";
            aux[index].multiple = event.target.value;
        } else {
            aux[index].value = event.target.value;
            aux[index].since = "";
            aux[index].until = "";
            aux[index].multiple = [];
        }

        setShowSkeleton(true);

        let objectFinal = {};

        aux.forEach((item) => {
            const query = attributes.find((item2) => item2.id === item.type).query;
            if (item.value && item.type) {
                objectFinal[query] = item.value;
            }
            if (item.type && item.since) {
                objectFinal[query + "_since"] = query !== "balance" ? item.since.format("YYYY-MM-DD") : item.since;
            }
            if (item.type && item.until) {
                objectFinal[query + "_until"] = query !== "balance" ? item.until.format("YYYY-MM-DD") : item.until;
            }
            if (item.multiple && item.multiple.length > 0) {
                let auxArr = [];
                const finalIDS = item.multiple.map((item) => {
                    if (item.includes(",")) {
                        auxArr = auxArr.concat(item.split(","));
                    }
                    return item;
                }).filter(item => !item.includes(","));
                
                objectFinal[query] = finalIDS.concat(auxArr);
            }
        });

        setFilters({
            ...filters,
            filtersArray: aux,
        });

        await dashboardService
            .getDashboard({
                company_id: company ?? undefined,
                franchise_id: franchise ?? undefined,
                grouped: grupal ?? false,
                ...objectFinal,
            })
            .then((response) => {
                if (response.length > 0) {
                    setDashboard(response);
                } else {
                    setDashboard([]);
                }
            })
            .catch((err) => {
                console.log(err);
            });

        setShowSkeleton(false);
    };

    const handleChangeFilterRange = async (event, index, filterType, range) => {
        let aux = [...filters.filtersArray];
        switch (filterType) {
            case 5: // Saldo final
                aux[index][range] = event.target.value;
                break;
            case 3: // Ultimo movimiento
            case 4: // Ultima conver.
            case 6: // Periodo
                aux[index][range] = event;
                break;
            default:
                break;
        }

        aux[index].value = "";

        setShowSkeleton(true);

        let objectFinal = {};

        aux.forEach((item) => {
            const query = attributes.find((item2) => item2.id === item.type).query;
            if (item.value && item.type) {
                objectFinal[query] = item.value;
            }
            if (item.type && item.since) {
                objectFinal[query + "_since"] = query !== "balance" ? item.since.format("YYYY-MM-DD") : item.since;
            }
            if (item.type && item.until) {
                objectFinal[query + "_until"] = query !== "balance" ? item.until.format("YYYY-MM-DD") : item.until;
            }
            if (item.multiple && item.multiple.length > 0) {
                objectFinal[query] = item.multiple;
            }
        });

        setFilters({
            ...filters,
            filtersArray: aux,
        });

        await dashboardService
            .getDashboard({
                company_id: company ?? undefined,
                franchise_id: franchise ?? undefined,
                grouped: grupal ?? false,
                ...objectFinal,
            })
            .then((response) => {
                if (response.length > 0) {
                    setDashboard(response);
                } else {
                    setDashboard([]);
                }
            })
            .catch((err) => {
                console.log(err);
            });

        setShowSkeleton(false);
    };

    const cleanFilters = async (e) => {
        if (e.target.classList.contains("MuiAvatar-img")) {
            setShowSkeleton(true);
            setFilters({
                filtersArray: [
                    {
                        type: "",
                        value: "",
                        since: "",
                        until: "",
                        multiple: [],
                    },
                ],
            });

            await dashboardService
                .getDashboard({
                    company_id: company ?? undefined,
                    franchise_id: franchise ?? undefined,
                    grouped: grupal ?? false,
                })
                .then((response) => {
                    if (response.length > 0) {
                        setDashboard(response);
                    } else {
                        setDashboard([]);
                    }
                })
                .catch((err) => {
                    console.log(err);
                });

            setShowSkeleton(false);
        }
    };

    useEffect(() => {
        setFilters({
            filtersArray: [
                {
                    type: "",
                    value: "",
                    since: "",
                    until: "",
                    multiple: [],
                },
            ],
        });
    }, [company, franchise, grupal]);

    return (
        <FiltersPopover
            id={id}
            open={open}
            anchorEl={anchorEl}
            handleClose={handleClose}
            cleanFilters={cleanFilters}
            handleClick={handleClick}
            length={
                filters.filtersArray.filter(
                    (item) => item.type && (item.value || item.since || item.until || item.multiple.length > 0)
                ).length
            }
            addFilter={addFilter}
            disabled={filters.filtersArray.length === limit}
            disabledButton={showSkeleton}
        >
            {filters.filtersArray.map((value, index) => (
                <Grid container spacing={2} alignItems="center" key={"dashComponent_" + index} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={11}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <BaseSelect2
                                    size="small"
                                    value={value.type}
                                    onChange={(e) => handleChangeType(e, index)}
                                    label={t("filters.attribute")}
                                >
                                    {value.type !== "" ?
                                        attributes.filter(
                                            (item) => !filters.filtersArray
                                                .map((item) => item.type)
                                                .includes(item.id)
                                        )
                                        .concat(attributes.filter((item) => item.id === value.type))
                                        .map((item) => (
                                            <MenuItem value={item.id} key={"attr_" + item.id}>
                                                <Typography
                                                    variant="body1"
                                                    component="span"
                                                    align="center"
                                                    color="textPrimary"
                                                    sx={{
                                                        fontStyle: "normal",
                                                    }}
                                                >
                                                    {item.name}
                                                </Typography>
                                            </MenuItem>
                                        ))
                                    : attributes.filter(
                                        (item) => !filters.filtersArray
                                            .map((item) => item.type)
                                            .includes(item.id)
                                    )
                                    .map((item) => (
                                        <MenuItem value={item.id} key={"attr_" + item.id}>
                                            <Typography
                                                variant="body1"
                                                component="span"
                                                align="center"
                                                color="textPrimary"
                                                sx={{
                                                    fontStyle: "normal",
                                                }}
                                            >
                                                {item.name}
                                            </Typography>
                                        </MenuItem>
                                    ))}
                                </BaseSelect2>
                            </Grid>
                            {value.type === "" && (
                                <Grid item xs={12} md={6}>
                                    <BaseSelect2
                                        size="small"
                                        value={value.type}
                                        label={t("filters.select")}
                                        onChange={(e) => handleChangeType(e, index)}
                                        values={[]}
                                        disabled
                                    />
                                </Grid>
                            )}
                            {value.type === 1 && ((franchise && !company) || grupal) && (
                                <Grid item xs={12} md={6}>
                                    <BaseSelect2
                                        label={t("filters.select")}
                                        multiple
                                        onChange={(e) => handleChangeFilter(e, index, value.type)}
                                        renderValue={(selected) => {
                                            return selected
                                                .map((item) => {
                                                    return companiesFilters.filter((item2) => item2.id === item)[0].name;
                                                })
                                                .join(", ");
                                        }}
                                        size="small"
                                        value={value.multiple}
                                    >
                                        {companiesFilters.map((item) => (
                                            <MenuItem value={item.id} key={"company_" + item.id}>
                                                <Typography
                                                    variant="body1"
                                                    component="span"
                                                    align="center"
                                                    color="textPrimary"
                                                    sx={{ fontStyle: "normal" }}
                                                >
                                                    <Checkbox checked={value.multiple.includes(item.id)} />
                                                    {item.name}
                                                </Typography>
                                            </MenuItem>
                                        ))}
                                    </BaseSelect2>
                                </Grid>
                            )}
                            {value.type === 2 && (
                                <Grid item xs={12} md={6}>
                                    <BaseSelect2
                                        label={t("filters.select")}
                                        multiple
                                        onChange={(e) => handleChangeFilter(e, index, value.type)}
                                        renderValue={(selected) => {
                                            return selected
                                                .map((item) => {
                                                    return banks.find((item2) => item2._id === item).bank_name;
                                                })
                                                .join(", ");
                                        }}
                                        size="small"
                                        value={value.multiple}
                                    >
                                        {banks.map((item) => (
                                            <MenuItem value={item._id} key={"banks_" + item._id}>
                                                <Typography
                                                    variant="body1"
                                                    component="span"
                                                    align="center"
                                                    color="textPrimary"
                                                    sx={{
                                                        fontStyle: "normal",
                                                    }}
                                                >
                                                    <Checkbox checked={value.multiple.includes(item._id)} />
                                                    {item.bank_name}
                                                </Typography>
                                            </MenuItem>
                                        ))}
                                    </BaseSelect2>
                                </Grid>
                            )}
                            {value.type === 5 && (
                                <>
                                    <Grid item xs={6} md={3}>
                                        <CustTextField
                                            fullWidth
                                            label={`${t("filters.value")} ${t("filters.since").toLowerCase()}`}
                                            onChange={(e) => handleChangeFilterRange(e, index, value.type, "since")}
                                            type="number"
                                            value={value.since}
                                        />
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <CustTextField
                                            fullWidth
                                            label={`${t("filters.value")} ${t("filters.until").toLowerCase()}`}
                                            onChange={(e) => handleChangeFilterRange(e, index, value.type, "until")}
                                            type="number"
                                            value={value.until}
                                        />
                                    </Grid>
                                </>
                            )}
                            {(value.type === 3 || value.type === 4) && (
                                <>
                                    <Grid item xs={6} md={3}>
                                        <CustomDatePicker
                                            label={t("filters.since")}
                                            maxDate={
                                                filters.filtersArray[index]?.until
                                                    ? filters.filtersArray[index].until
                                                    : null
                                            }
                                            onChange={(e) => handleChangeFilterRange(e, index, value.type, "since")}
                                            value={value.since ? value.since : null}
                                        />
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <CustomDatePicker
                                            label={t("filters.until")}
                                            minDate={
                                                filters.filtersArray[index]?.since
                                                    ? filters.filtersArray[index].since
                                                    : null
                                            }
                                            onChange={(e) => handleChangeFilterRange(e, index, value.type, "until")}
                                            value={value.until ? value.until : null}
                                        />
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </Grid>
                    <Grid item xs={12} md={1} display="flex" justifyContent="center">
                        {filters.filtersArray.length > 1 && (
                            <DeleteIcon
                                color="primary"
                                sx={{ cursor: "pointer" }}
                                onClick={() => removeFilter(index)}
                            />
                        )}
                        {filters.filtersArray.length === 1 &&
                            (value.value !== "" || value.since !== "" || value.until !== "" || value.multiple.length > 0) && (
                                <DeleteIcon
                                    color="primary"
                                    sx={{ cursor: "pointer" }}
                                    onClick={() => removeFirstFilter()}
                                />
                            )}
                    </Grid>
                </Grid>
            ))}
        </FiltersPopover>
    );
};

export default FiltersButton;
